'use client';

/**
 * One piece of state for every help surface in the app: which doc is showing,
 * and which section it should be scrolled to.
 *
 * Two things fall out of centralising it that were impossible before:
 *
 *  - The (i) is a TOGGLE. Each surface used to own a private `open` boolean, so
 *    a second click on the same trigger re-opened what was already open. Here a
 *    trigger that names the doc already showing closes it instead.
 *
 *  - A heading's (i) is a deep LINK, not just an opener. The trigger passes its
 *    section anchor, and the host scrolls there — whether the panel was already
 *    open (scroll now) or is opening from cold (scroll once the body mounts).
 *
 * Hosts: most pages get the dock AppShell renders. A page that already owns a
 * docked right panel — the agent builder — calls useHelpHost() to claim the
 * content, and AppShell's dock stays out of its way. That claim is what stops
 * the two panels stacking, which is the bug this whole change exists to fix.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { usePathname } from 'next/navigation';
import type { HelpDoc } from './help-doc';

interface HelpTarget {
  docKey: string;
  /** Section id to scroll to once rendered; null means "top of the doc". */
  anchor: string | null;
  /** Bumped on every request so a repeat jump to the same anchor re-fires. */
  nonce: number;
  /**
   * True when a trigger beside one heading opened the panel: the panel then shows
   * that one section alone, with a link to the whole reference, instead of the
   * whole reference scrolled to it (12 Oct 2026: readers lost the section they
   * had asked about inside a long tab reference).
   */
  focus: boolean;
}

interface HelpContextValue {
  target: HelpTarget | null;
  /** Open `docKey`, or close it if it is already the one showing. */
  toggleHelp: (docKey: string, anchor?: string) => void;
  /** Always open (used by heading links, where closing would be surprising). */
  openHelp: (docKey: string, anchor?: string) => void;
  closeHelp: () => void;
  /** Scroll the open doc to a section without changing which doc is open. */
  jumpTo: (sectionId: string) => void;
  /** Leave the one-section view and show the whole reference, parked at that section. */
  showAll: () => void;
  /**
   * A doc supplied by the caller instead of the registry. Some surfaces author
   * their help as JSX inside the component that owns the screen — the gateway
   * settings pages pass a `docs` node per tab — and there is no key a registry
   * loader could resolve to that. They hand the whole doc over instead.
   */
  inlineDoc: HelpDoc | null;
  toggleInlineHelp: (doc: HelpDoc) => void;
  /** True when a page-local panel has claimed rendering. */
  hasLocalHost: boolean;
  claimLocalHost: () => () => void;
}

const HelpContext = createContext<HelpContextValue | null>(null);

export function HelpProvider({ children }: { children: React.ReactNode }) {
  const [target, setTarget] = useState<HelpTarget | null>(null);
  const [inlineDoc, setInlineDoc] = useState<HelpDoc | null>(null);
  const [localHosts, setLocalHosts] = useState(0);

  const openHelp = useCallback((docKey: string, anchor?: string) => {
    setTarget((prev) => ({
      docKey,
      anchor: anchor ?? null,
      nonce: (prev?.nonce ?? 0) + 1,
      focus: !!anchor,
    }));
  }, []);

  const toggleHelp = useCallback((docKey: string, anchor?: string) => {
    setTarget((prev) => {
      // A trigger closes what IT opened — same doc, same section. Comparing the
      // anchor too lets a page title that deep-links (Settings, which points at
      // whichever category you are in) still act as an on/off switch, while a
      // heading elsewhere in the doc correctly re-aims instead of closing.
      const sameAnchor = (prev?.anchor ?? null) === (anchor ?? null);
      if (prev && prev.docKey === docKey && sameAnchor) return null;
      return { docKey, anchor: anchor ?? null, nonce: (prev?.nonce ?? 0) + 1, focus: !!anchor };
    });
  }, []);

  const toggleInlineHelp = useCallback((doc: HelpDoc) => {
    setTarget((prev) => {
      if (prev && prev.docKey === doc.key) { setInlineDoc(null); return null; }
      setInlineDoc(doc);
      return { docKey: doc.key, anchor: null, nonce: (prev?.nonce ?? 0) + 1, focus: false };
    });
  }, []);

  const closeHelp = useCallback(() => { setTarget(null); setInlineDoc(null); }, []);

  const jumpTo = useCallback((sectionId: string) => {
    setTarget((prev) => (prev ? { ...prev, anchor: sectionId, nonce: prev.nonce + 1, focus: false } : prev));
  }, []);

  const showAll = useCallback(() => {
    setTarget((prev) => (prev ? { ...prev, nonce: prev.nonce + 1, focus: false } : prev));
  }, []);

  const claimLocalHost = useCallback(() => {
    setLocalHosts((n) => n + 1);
    return () => setLocalHosts((n) => Math.max(0, n - 1));
  }, []);

  // ⚠ Drop the open doc when the route changes.
  //
  // Doc keys are page-scoped ("agent:overview", "kb:embeddings", "projects"),
  // but the provider lives above the router, so without this a doc opened on
  // one page stays open on the next and the dock renders it there. It showed up
  // as the AGENT overview reference ("Agent name", "Agent icon") sitting on a
  // Knowledgebase screen after navigating between the two.
  const pathname = usePathname();
  useEffect(() => { setTarget(null); setInlineDoc(null); }, [pathname]);

  // Esc closes from anywhere. The panel is docked, not modal, so it does not
  // trap focus or lock scroll — Esc is a convenience, not the only way out.
  useEffect(() => {
    if (!target) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeHelp(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [target, closeHelp]);

  const value = useMemo<HelpContextValue>(() => ({
    target,
    toggleHelp,
    openHelp,
    closeHelp,
    jumpTo,
    showAll,
    inlineDoc,
    toggleInlineHelp,
    hasLocalHost: localHosts > 0,
    claimLocalHost,
  }), [target, toggleHelp, openHelp, closeHelp, jumpTo, showAll, inlineDoc, toggleInlineHelp, localHosts, claimLocalHost]);

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
}

/**
 * Safe outside a provider: help triggers are sprinkled through components that
 * also render in tests, storybooks and the docs site, and a missing provider
 * should make the (i) inert rather than crash the page.
 */
export function useHelp(): HelpContextValue {
  const ctx = useContext(HelpContext);
  return ctx ?? FALLBACK;
}

const noop = () => {};
const FALLBACK: HelpContextValue = {
  target: null,
  toggleHelp: noop,
  openHelp: noop,
  closeHelp: noop,
  jumpTo: noop,
  showAll: noop,
  inlineDoc: null,
  toggleInlineHelp: noop,
  hasLocalHost: false,
  claimLocalHost: () => noop,
};

/** Claim help rendering for a page that already has its own docked panel. */
export function useHelpHost(active: boolean) {
  const { claimLocalHost } = useHelp();
  useEffect(() => {
    if (!active) return;
    return claimLocalHost();
  }, [active, claimLocalHost]);
}
